import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { memo, useMemo, useState } from "react";
import { IGeographyItem } from "../../../interfaces/dashboard";
import {
  formatNumber,
  formatRate,
  formatDays,
  formatCurrency,
} from "../../../utils/metricsFormatters";
import { LOCALE } from "../../../utils/constants";
import { IProvinceLabel } from "../../../utils/common";


interface IGeographyProps {
  data: IGeographyItem[];
  provinceLabel: IProvinceLabel;
}

type TSortKey = keyof IGeographyItem;
type TSortDir = "asc" | "desc";

const numericCompare = (av: string | number, bv: string | number): number => {
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
};

const compareFn = (
  av: string | number,
  bv: string | number,
  sortAsc: boolean,
): number => {
  const cmp =
    typeof av === "string" && typeof bv === "string"
      ? av.localeCompare(bv, LOCALE)
      : numericCompare(av, bv);
  return sortAsc ? cmp : -cmp;
};

const sortDir = (isActive: boolean, sortAsc: boolean): TSortDir => {
  if (isActive && sortAsc) return "asc";
  return "desc";
};

// El podio (top 3) se resalta con color para leer el ranking de un vistazo,
// como pediría un heatmap pero sin depender de geometría (ver V2 en
// docs/DASHBOARD_GEOGRAPHY_V2.md y el componente GeographyMap.tsx reservado).
const RANK_COLORS = ["#0D47A1", "#1976D2", "#42A5F5"];
const rankColor = (index: number): string => RANK_COLORS[index] ?? "#90A4AE";

const Geography = ({ data, provinceLabel }: IGeographyProps) => {
  const [sortKey, setSortKey] = useState<TSortKey>("count");
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: TSortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  // Memoizado: copia + sort en cada render; solo depende de datos y criterio.
  const sorted = useMemo(
    () =>
      [...data].sort((a, b) =>
        compareFn(
          (a[sortKey] ?? 0) as string | number,
          (b[sortKey] ?? 0) as string | number,
          sortAsc,
        ),
      ),
    [data, sortKey, sortAsc],
  );

  const col = (key: TSortKey, label: string) => {
    const active = sortKey === key;
    return (
      <TableCell sortDirection={active ? sortDir(active, sortAsc) : false}>
        <TableSortLabel
          active={active}
          direction={sortDir(active, sortAsc)}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            {col("province", provinceLabel.singular)}
            {col("count", "Envíos")}
            {col("percentage", "% del total")}
            {col("slaComplianceRate", "Cumplimiento SLA")}
            {col("avgDeliveryDays", "Tiempo prom.")}
            {col("avgCost", "Costo prom.")}
            {col("successRate", "% de éxito")}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, index) => (
            <TableRow key={row.province} hover>
              <TableCell>
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{
                    height: 22,
                    minWidth: 22,
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: rankColor(index),
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={index < 3 ? 600 : 400}>
                  {row.province}
                </Typography>
              </TableCell>
              <TableCell>{formatNumber(row.count)}</TableCell>
              <TableCell>{formatRate(row.percentage)}</TableCell>
              <TableCell>{formatRate(row.slaComplianceRate)}</TableCell>
              <TableCell>{formatDays(row.avgDeliveryDays)}</TableCell>
              <TableCell>
                {row.avgCost === null ? "—" : formatCurrency(row.avgCost)}
              </TableCell>
              <TableCell>{formatRate(row.successRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default memo(Geography);
